import { ApplicationRef, EnvironmentInjector, Injectable, createComponent } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { RemmiConfirmComponent } from './remmi-confirm.component';
import { RemmiConfirmOptions } from './remmi-confirm.model';

const DEFAULTS: RemmiConfirmOptions = {
  title: 'Are You Sure?',
  confirmLabel: 'Yes',
  cancelLabel: 'Cancel',
  tone: 'primary',
};

/** A prompt opened within this long after a click is treated as opened by that click. */
const CLICK_ANCHOR_WINDOW_MS = 1500;
const CLICKABLE = 'button, a, [role="button"], [role="menuitem"], .p-menuitem-link, .p-chip, img, i';

/**
 * Opens the shared `RemmiConfirmComponent`. Emits once (true = confirmed) and completes, so callers
 * don't need to unsubscribe.
 *
 * ```ts
 * this.remmiConfirm.delete({ subject: tag.name }).subscribe(ok => ok && this.deleteTag(tag._id));
 * ```
 */
@Injectable({ providedIn: 'root' })
export class RemmiConfirmService {
  /**
   * Last click's element rect, captured at click time. The popover anchors to it without ~145 call
   * sites having to pass their event in. The rect is kept, not the element, because menu items that
   * trigger a delete are usually gone from the DOM by the time the prompt opens.
   */
  private lastClick: { rect: DOMRect; at: number } = null;

  constructor(
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector
  ) {
    document.addEventListener('click', event => this.rememberClick(event), true);
  }

  ask(options: RemmiConfirmOptions = {}): Observable<boolean> {
    const result = new Subject<boolean>();
    const componentRef = createComponent(RemmiConfirmComponent, { environmentInjector: this.environmentInjector });

    componentRef.instance.options = { ...DEFAULTS, ...options };
    componentRef.instance.anchor = this.resolveAnchor(options.target);
    componentRef.instance.result = result;
    this.appRef.attachView(componentRef.hostView);
    document.body.appendChild(componentRef.location.nativeElement);

    result.subscribe({
      complete: () => {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
      },
    });

    return result.asObservable();
  }

  /** Delete preset for anything that deletes or removes data. */
  delete(options: RemmiConfirmOptions = {}): Observable<boolean> {
    return this.ask({
      title: 'Delete Item?',
      confirmLabel: 'Delete',
      tone: 'danger',
      ...options,
    });
  }

  private rememberClick(event: MouseEvent) {
    const element = (event.target as HTMLElement)?.closest?.(CLICKABLE) ?? (event.target as HTMLElement);
    const rect = element?.getBoundingClientRect?.();
    this.lastClick = rect?.width ? { rect, at: Date.now() } : null;
  }

  private resolveAnchor(target: Event | HTMLElement): DOMRect {
    const element = target instanceof Event ? (target.currentTarget ?? target.target) : target;
    if (element instanceof HTMLElement && element.isConnected) {
      return element.getBoundingClientRect();
    }
    if (this.lastClick && Date.now() - this.lastClick.at < CLICK_ANCHOR_WINDOW_MS) {
      return this.lastClick.rect;
    }
    return null;
  }
}
